// @flow
import type { ChildrenArray, Element } from 'react';
import type { MathString } from '../../types.js';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { productColorSunflower } from '../../designSystem.js';
import { InternalHeading } from '../../InternalHeading.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import TabularData from '../../TabularData.js';
import { sharedRSLChildrenElementTypes } from '../common-children-types.js';
import type { SharedRSLChildren } from '../common-children-types.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import { mathStringChecker } from '../../types.js';

export function RSLTNIdeaExploration(props: {
  groupSize: 'individual' | 'pairs' | 'small',
  children: ChildrenArray<
    | Element<typeof RSLTNExplorationExplanation>
    | Element<typeof RSLTNIdealResponses>
    | Element<typeof RSLTNPurposefulQuestions>
    | Element<typeof RSLTNPossibleMisunderstandings>,
  >,
  ...CompilerInjectedProps,
}) {
  const [overview, ...remainingChildren] = React.Children.toArray(
    props.children,
  );

  const groupSizeMapping = {
    individual: 'Individual',
    pairs: 'In pairs',
    small: 'Small groups',
  };
  const { failedValidation } = usePropsValidation(props, RSLTNIdeaExploration);

  if (failedValidation) {
    return null;
  }
  return (
    <div
      style={{
        // TODO this is NOT a sunflower color, its a teacher-only
        // color from classic mathspace
        backgroundColor: '#F3F9FF', // teacher/aliceBlue
        borderTop: '2px solid',
        borderBottom: '2px solid',
        // TODO this is NOT a sunflow colors, its a teacher-ronly
        // color from classic mathspace
        borderColor: '#195E8C', // teacher/matisse
        padding: '0 21px 16px 21px',
        margin: '25px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div>
        <InternalHeading level={3}>Exploration</InternalHeading>
        {props.groupSize && (
          <p style={{ color: productColorSunflower.grey90, marginTop: -16 }}>
            <span style={{ fontWeight: 600 }}>Suggested student grouping</span>:{' '}
            {groupSizeMapping[props.groupSize]}
          </p>
        )}
        {overview}
      </div>
      {remainingChildren}
    </div>
  );
}
RSLTNIdeaExploration.spec = {
  groupSize: t.unionOfValues('individual', 'pairs', 'small'),
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(RSLTNExplorationExplanation) },
    { min: 0, max: 1, elementType: t.elementType(RSLTNIdealResponses) },
    { min: 0, max: 1, elementType: t.elementType(RSLTNPurposefulQuestions) },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(RSLTNPossibleMisunderstandings),
    },
  ),
};

export function RSLTNExplorationExplanation(props: {
  children: ChildrenArray<SharedRSLChildren>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNExplorationExplanation,
  );

  if (failedValidation) return null;

  return <div>{props.children}</div>;
}
RSLTNExplorationExplanation.spec = {
  children: t.childrenArray(t.unionOfTypes(...sharedRSLChildrenElementTypes)),
};

// This section has a less than ideal structure as a result
// of a main use case for the lesson exploration not being specified,
// namely asking the student to provide solutions for problems.
// The result is that content authors have so far used a combination
// numbered Lists for asking text based questions and free-floating
// tables and other figures that are to be completed by the student.
// Enforcing some structure while allowing numbering and other formatting
// to correspond has meant compromises needed to be made here.
export function RSLTNIdealResponses(props: {
  children: ChildrenArray<
    | Element<typeof RSLTNCompletedFigures>
    | Element<typeof RSLTNQuestionWithResponses>,
  >,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNIdealResponses);

  if (failedValidation) return null;
  const [completedFigures, ...remainingChildren] = React.Children.toArray(
    props.children,
  );
  const hasCompletedFigures =
    completedFigures.type.name === 'RSLTNCompletedFigures';

  return (
    <div>
      <p style={{ fontWeight: 600, margin: 0 }}>Ideal student responses</p>
      <p style={{ marginTop: 10 }}>
        These ideal responses may differ from other correct student responses.
        Less formal responses can be connected with the more precise
        mathematical language presented here.
      </p>
      {hasCompletedFigures && completedFigures}
      <ol style={{ paddingLeft: 30, listStyle: 'decimal' }}>
        {React.Children.map(
          hasCompletedFigures ? remainingChildren : props.children,
          (child, idx) => (
            <li style={{ fontWeight: 600 }} key={idx}>
              {child}
            </li>
          ),
        )}
      </ol>
    </div>
  );
}
RSLTNIdealResponses.spec = {
  children: childrenArrayMatcher(
    { min: 0, max: 1, elementType: t.elementType(RSLTNCompletedFigures) },
    {
      min: 1,
      max: Infinity,
      elementType: t.elementType(RSLTNQuestionWithResponses),
    },
  ),
};

export function RSLTNPurposefulQuestions(props: {
  children: ChildrenArray<Element<typeof RSLTNPurposefulQuestion>>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNPurposefulQuestions,
  );

  if (failedValidation) return null;
  return (
    <div>
      <p style={{ fontWeight: 600, margin: 0 }}>Purposeful questions</p>
      <ul style={{ listStyle: 'disc' }}>
        {React.Children.map(props.children, (child) => (
          <li>{child}</li>
        ))}
      </ul>
    </div>
  );
}
RSLTNPurposefulQuestions.spec = {
  children: t.childrenArray(t.elementType(RSLTNPurposefulQuestion)),
};

export function RSLTNPossibleMisunderstandings(props: {
  children: ChildrenArray<Element<typeof RSLTNMisunderstanding>>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNPossibleMisunderstandings,
  );

  if (failedValidation) return null;
  return (
    <div>
      <p style={{ fontWeight: 600, margin: 0 }}>Possible misunderstandings</p>
      <ul style={{ listStyle: 'disc' }}>
        {React.Children.map(props.children, (child) => (
          <li>{child}</li>
        ))}
      </ul>
    </div>
  );
}
RSLTNPossibleMisunderstandings.spec = {
  children: t.childrenArray(t.elementType(RSLTNMisunderstanding)),
};

export function RSLTNCompletedFigures(props: {
  children: ChildrenArray<React$Element<typeof RSLTNCompletedFigure>>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNCompletedFigures);

  if (failedValidation) return null;
  return (
    <div>
      <p>The student is asked to complete the following figures:</p>
      {props.children}
    </div>
  );
}
RSLTNCompletedFigures.spec = {
  children: t.childrenArray(t.elementType(RSLTNCompletedFigure)),
};

export function RSLTNCompletedFigure(props: {|
  children: [
    Element<typeof RSLTNCompletedFigureAnnotation>,
    Element<typeof CartesianPlane> | Element<typeof TabularData>,
  ],
  ...CompilerInjectedProps,
|}) {
  const [annotation, figure] = React.Children.toArray(props.children);

  const { failedValidation } = usePropsValidation(props, RSLTNCompletedFigure);

  if (failedValidation) return null;
  return (
    <div style={{ padding: '0 24px' }}>
      <div style={{ marginBottom: -12 }}>{annotation}</div>
      <div>{figure}</div>
    </div>
  );
}
// Only tables and graphs are allowed in order to "cut down on scope"
RSLTNCompletedFigure.spec = {
  children: t.unionOfTypes(
    t.exactOrderChildrenArray(
      t.elementType(RSLTNCompletedFigureAnnotation),
      t.elementType(CartesianPlane),
    ),
    t.exactOrderChildrenArray(
      t.elementType(RSLTNCompletedFigureAnnotation),
      t.elementType(TabularData),
    ),
  ),
};

export function RSLTNCompletedFigureAnnotation(props: {|
  children: ChildrenArray<MathString>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNCompletedFigureAnnotation,
  );

  if (failedValidation) return null;
  return props.children;
}
RSLTNCompletedFigureAnnotation.spec = {
  children: t.childrenArray(mathStringChecker),
};

export function RSLTNQuestionWithResponses(props: {
  children: ChildrenArray<
    Element<typeof RSLTNGivenQuestion> | Element<typeof RSLTNResponse>,
  >,
  ...CompilerInjectedProps,
}) {
  const [givenQuestion, ...responses] = React.Children.toArray(props.children);

  const { failedValidation } = usePropsValidation(
    props,
    RSLTNQuestionWithResponses,
  );

  if (failedValidation) return null;
  return (
    <div style={{ marginLeft: 4 }}>
      <p style={{ marginBottom: 8 }}>{givenQuestion}</p>
      {responses.map((response, idx) => (
        <p key={idx} style={{ margin: 8, fontWeight: 'normal' }}>
          {response}
        </p>
      ))}
    </div>
  );
}
RSLTNQuestionWithResponses.spec = {
  children: childrenArrayMatcher(
    { min: 1, max: 1, elementType: t.elementType(RSLTNGivenQuestion) },
    { min: 1, max: Infinity, elementType: t.elementType(RSLTNResponse) },
  ),
};

export function RSLTNPurposefulQuestion(props: {
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNPurposefulQuestion,
  );
  if (failedValidation) return null;
  return props.children;
}
RSLTNPurposefulQuestion.spec = { children: t.childrenArray(mathStringChecker) };

export function RSLTNMisunderstanding(props: {
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNMisunderstanding);
  if (failedValidation) return null;
  return props.children;
}
RSLTNMisunderstanding.spec = { children: t.childrenArray(mathStringChecker) };

export function RSLTNGivenQuestion(props: {
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNGivenQuestion);

  if (failedValidation) return null;
  return <span style={{ fontWeight: 600 }}>{props.children}</span>;
}
RSLTNGivenQuestion.spec = { children: t.childrenArray(mathStringChecker) };

export function RSLTNResponse(props: {
  children: ChildrenArray<MathString>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNResponse);

  if (failedValidation) return null;
  return props.children;
}
RSLTNResponse.spec = { children: t.childrenArray(mathStringChecker) };
